import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  Activity,
  Search,
  Filter,
  RefreshCw,
  Download,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Info,
  XCircle,
  Clock,
  Server,
  Database,
  Zap,
  Eye,
  X,
  TrendingUp,
  BarChart3,
  PieChart,
  Play,
  Pause,
  Bell,
  BellOff
} from 'lucide-react';
import { notify } from '@core/notifications/notify';
import PageInfoButton from '@/components/PageInfoButton';

interface SystemLog {
  id: number;
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug' | 'critical';
  message: string;
  service: string;
  environment: string;
  request_id?: string;
  user_id?: number;
  route?: string;
  method?: string;
  status_code?: number;
  stack?: string;
  category?: string;
  severity?: string;
  ip?: string;
  user_agent?: string;
  metadata?: any;
}

interface SystemHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  uptime: number;
  version: string;
  environment: string;
  last_check: string;
}

const SystemLogs: React.FC = () => {
  const { hasPermission, isLoading: authLoading } = useAuth();
  const canViewSystemLogs = hasPermission('system_logs.view');
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterService, setFilterService] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [selectedLog, setSelectedLog] = useState<any | null>(null);
  const [sortField, setSortField] = useState<'timestamp' | 'level' | 'service'>('timestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // New state for enhanced features
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval] = useState(30000); // 30 seconds
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('1h');
  const [alertsEnabled, setAlertsEnabled] = useState(true);

  useEffect(() => {
    loadData();
  }, [pagination.page, pagination.limit, selectedTimeRange]);

  // Auto-refresh effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadLogs();
      }, refreshInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, refreshInterval]);

  // Analytics calculations
  const analytics = useMemo(() => {
    const totalLogs = logs.length;
    const errorCount = logs.filter(log => log.level === 'error').length;
    const warningCount = logs.filter(log => log.level === 'warn').length;
    const criticalCount = logs.filter(log => log.level === 'critical').length;

    const errorRate = totalLogs > 0 ? (errorCount / totalLogs) * 100 : 0;

    // Service distribution
    const serviceCounts = logs.reduce((acc, log) => {
      acc[log.service] = (acc[log.service] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topServices = Object.entries(serviceCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);

    // Category distribution
    const categoryCounts = logs.reduce((acc, log) => {
      const category = log.category || 'uncategorized';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalLogs,
      errorCount,
      warningCount,
      criticalCount,
      errorRate,
      topServices,
      categoryCounts,
      avgLogsPerHour: totalLogs / 24 // Rough estimate
    };
  }, [logs]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadLogs(),
        loadSystemHealth(),
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const getTimeRangeDates = (timeRange: string) => {
    const now = new Date();
    let startDate: Date;

    switch (timeRange) {
      case '1h':
        startDate = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case '6h':
        startDate = new Date(now.getTime() - 6 * 60 * 60 * 1000);
        break;
      case '24h':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    }

    return {
      startDate: startDate.toISOString(),
      endDate: now.toISOString()
    };
  };

  const loadLogs = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });

      // Add time range filter
      const { startDate: timeStart, endDate: timeEnd } = getTimeRangeDates(selectedTimeRange);
      if (!filterStartDate && !filterEndDate) {
        params.append('startDate', timeStart);
        params.append('endDate', timeEnd);
      }

      // Add filters if they exist
      if (filterLevel) params.append('level', filterLevel);
      if (filterService) params.append('service', filterService);
      if (filterCategory) params.append('category', filterCategory);
      if (filterSeverity) params.append('severity', filterSeverity);
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);

      const response = await fetch(`/api/admin/system-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setLogs(result.data || []);
        setPagination(result.pagination || pagination);
      } else {
        notify.error('Failed to load system logs');
      }
    } catch (error) {
      console.error('Error loading system logs:', error);
      notify.error('Failed to load system logs');
    }
  };

  const loadSystemHealth = async () => {
    try {
      const response = await fetch('/api/admin/system-logs/health', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setSystemHealth(result.data || null);
      }
    } catch (error) {
      console.error('Error loading system health:', error);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchTerm ||
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.route && log.route.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesLevel = !filterLevel || log.level === filterLevel;
    const matchesService = !filterService || log.service === filterService;
    const matchesStartDate = !filterStartDate || new Date(log.timestamp) >= new Date(filterStartDate);
    const matchesEndDate = !filterEndDate || new Date(log.timestamp) <= new Date(filterEndDate);
    return matchesSearch && matchesLevel && matchesService && matchesStartDate && matchesEndDate;
  }).sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field: 'timestamp' | 'level' | 'service') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
      case 'critical':
        return <XCircle size={16} className="text-red-600" />;
      case 'warn':
      case 'warning':
        return <AlertTriangle size={16} className="text-amber-600" />;
      case 'info':
        return <Info size={16} className="text-blue-600" />;
      case 'debug':
        return <Activity size={16} className="text-slate-600" />;
      default:
        return <Info size={16} className="text-slate-600" />;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
      case 'critical':
        return 'bg-red-100 text-red-700';
      case 'warn':
      case 'warning':
        return 'bg-amber-100 text-amber-700';
      case 'info':
        return 'bg-blue-100 text-blue-700';
      case 'debug':
        return 'bg-slate-100 text-slate-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const getServiceIcon = (service: string) => {
    switch (service.toLowerCase()) {
      case 'api':
      case 'backend':
        return <Server size={16} className="text-purple-600" />;
      case 'database':
      case 'db':
        return <Database size={16} className="text-emerald-600" />;
      case 'auth':
        return <Zap size={16} className="text-amber-600" />;
      case 'health':
        return <Activity size={16} className="text-pink-600" />;
      default:
        return <Activity size={16} className="text-slate-600" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const handleExport = () => {
    const csv = [
      ['Timestamp', 'Level', 'Service', 'Message', 'Route', 'Method', 'Status', 'IP Address'].join(','),
      ...filteredLogs.map(log => [
        formatTimestamp(log.timestamp),
        log.level,
        log.service,
        `"${log.message.replace(/"/g, '""')}"`,
        log.route || '',
        log.method || '',
        log.status_code || '',
        log.ip || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `system-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!canViewSystemLogs) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="bg-red-600 px-6 py-5">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-white" size={24} />
              <div>
                <h1 className="text-lg font-bold text-white">Access Denied</h1>
                <p className="text-red-100 text-sm mt-0.5">Restricted Area</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <p className="text-slate-600 text-sm">Only Super Admin can access System Logs.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading system logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">System Logs</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm mt-1">Real-time monitoring and error analytics</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                autoRefresh
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {autoRefresh ? <Pause size={16} /> : <Play size={16} />}
              {autoRefresh ? 'Auto' : 'Manual'}
            </button>
            <button
              onClick={() => setAlertsEnabled(!alertsEnabled)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                alertsEnabled
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {alertsEnabled ? <Bell size={16} /> : <BellOff size={16} />}
              Alerts
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Quick Stats Bar */}
        <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Activity className="text-blue-600" size={16} />
                <span className="text-sm font-medium text-slate-700">
                  {analytics.totalLogs} Total Logs
                </span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="text-red-600" size={16} />
                <span className="text-sm font-medium text-slate-700">
                  {analytics.errorCount} Errors ({analytics.errorRate.toFixed(1)}%)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-amber-600" size={16} />
                <span className="text-sm font-medium text-slate-700">
                  {analytics.warningCount} Warnings
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="text-slate-500" size={16} />
                <span className="text-sm font-medium text-slate-700">
                  {autoRefresh ? `${refreshInterval / 1000}s refresh` : 'Manual refresh'}
                </span>
              </div>
            </div>
            <button
              onClick={loadData}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Error Rate Chart */}
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900">Error Analytics</h3>
                <BarChart3 className="text-slate-400" size={18} />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Error Rate</span>
                  <span className={`text-sm font-bold ${
                    analytics.errorRate > 10 ? 'text-red-600' :
                    analytics.errorRate > 5 ? 'text-amber-600' : 'text-green-600'
                  }`}>
                    {analytics.errorRate.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Critical Errors</span>
                  <span className="text-sm font-bold text-red-600">{analytics.criticalCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Avg Logs/Hour</span>
                  <span className="text-sm font-bold text-blue-600">{analytics.avgLogsPerHour.toFixed(1)}</span>
                </div>
              </div>
            </div>

            {/* Service Distribution */}
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900">Top Services</h3>
                <PieChart className="text-slate-400" size={18} />
              </div>
              <div className="space-y-3">
                {analytics.topServices.slice(0, 4).map(([service, count]) => (
                  <div key={service} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getServiceIcon(service)}
                      <span className="text-sm text-slate-900 capitalize">{service}</span>
                    </div>
                    <span className="text-sm font-bold text-slate-600">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900">Error Categories</h3>
                <TrendingUp className="text-slate-400" size={18} />
              </div>
              <div className="space-y-3">
                {Object.entries(analytics.categoryCounts).slice(0, 4).map(([category, count]) => (
                  <div key={category} className="flex items-center justify-between">
                    <span className="text-sm text-slate-900 capitalize">{category}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-slate-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full"
                          style={{ width: `${(count / analytics.totalLogs) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-bold text-slate-600 w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* System Health Card */}
        {systemHealth && (
          <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  systemHealth.status === 'healthy' ? 'bg-emerald-100' :
                  systemHealth.status === 'degraded' ? 'bg-amber-100' : 'bg-red-100'
                }`}>
                  <Activity className={`${
                    systemHealth.status === 'healthy' ? 'text-emerald-600' :
                    systemHealth.status === 'degraded' ? 'text-amber-600' : 'text-red-600'
                  }`} size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">System Health</h3>
                  <p className="text-xs text-slate-500">
                    Status: <span className={`font-bold ${
                      systemHealth.status === 'healthy' ? 'text-emerald-600' :
                      systemHealth.status === 'degraded' ? 'text-amber-600' : 'text-red-600'
                    }`}>{systemHealth.status.toUpperCase()}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-5 text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                  <Clock size={14} />
                  <span>Uptime: {formatUptime(systemHealth.uptime)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Server size={14} />
                  <span>v{systemHealth.version}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Activity size={14} />
                  <span>{systemHealth.environment}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Advanced Filters */}
        <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Filters & Search</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterLevel('');
                  setFilterService('');
                  setFilterCategory('');
                  setFilterSeverity('');
                  setFilterStartDate('');
                  setFilterEndDate('');
                  loadData();
                }}
                className="text-xs font-medium text-slate-500 hover:text-slate-900 underline"
              >
                Clear all
              </button>
              <button
                onClick={() => setShowAnalytics(!showAnalytics)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  showAnalytics
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <BarChart3 size={14} />
                Analytics
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Time Range</label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <select
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors appearance-none cursor-pointer"
                >
                  <option value="1h">Last Hour</option>
                  <option value="6h">Last 6 Hours</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Level</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <select
                  value={filterLevel}
                  onChange={(e) => setFilterLevel(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors appearance-none cursor-pointer"
                >
                  <option value="">All Levels</option>
                  <option value="info">Info</option>
                  <option value="warn">Warning</option>
                  <option value="error">Error</option>
                  <option value="debug">Debug</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Service</label>
              <div className="relative">
                <Server className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <select
                  value={filterService}
                  onChange={(e) => setFilterService(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors appearance-none cursor-pointer"
                >
                  <option value="">All Services</option>
                  <option value="api">API</option>
                  <option value="database">Database</option>
                  <option value="auth">Auth</option>
                  <option value="system">System</option>
                  <option value="backend">Backend</option>
                  <option value="health">Health</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Start Date</label>
              <input
                type="datetime-local"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">End Date</label>
              <input
                type="datetime-local"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterLevel('');
                  setFilterService('');
                  setFilterStartDate('');
                  setFilterEndDate('');
                }}
                className="w-full px-4 py-2.5 bg-slate-100 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="text-slate-400" size={18} />
                <h3 className="text-sm font-semibold text-slate-900">System Logs</h3>
              </div>
              <span className="px-2 py-1 rounded-md font-bold text-[10px] bg-blue-100 text-blue-700">
                {filteredLogs.length}
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-xs font-medium uppercase text-slate-500 border-b border-slate-100">
                <tr>
                  <th
                    className="px-5 py-3 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('timestamp')}
                  >
                    <div className="flex items-center gap-1.5">
                      <Clock size={13} />
                      Timestamp
                      {sortField === 'timestamp' && (
                        sortDirection === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-5 py-3 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('level')}
                  >
                    <div className="flex items-center gap-1.5">
                      <Activity size={13} />
                      Level
                      {sortField === 'level' && (
                        sortDirection === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-5 py-3 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('service')}
                  >
                    <div className="flex items-center gap-1.5">
                      <Server size={13} />
                      Service
                      {sortField === 'service' && (
                        sortDirection === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                      )}
                    </div>
                  </th>
                  <th className="px-5 py-3">Message</th>
                  <th className="px-5 py-3">Request</th>
                  <th className="px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <Activity className="text-slate-300 mb-3" size={36} />
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">No system logs found</h3>
                      <p className="text-sm text-slate-500">
                        {searchTerm || filterLevel || filterService || filterStartDate || filterEndDate
                          ? 'Try adjusting your search criteria'
                          : 'No system logs available'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3 whitespace-nowrap text-left text-sm text-slate-900">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="px-5 py-3 text-left">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full ${getLevelColor(log.level)}`}>
                            {getLevelIcon(log.level)}
                            {log.level.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-left">
                          <div className="flex items-center gap-1.5">
                            {getServiceIcon(log.service)}
                            <span className="text-sm text-slate-900">{log.service}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-left text-sm text-slate-600 max-w-xs truncate">
                          {log.message}
                        </td>
                        <td className="px-5 py-3 text-left text-sm text-slate-500 font-mono">
                          {log.request_id ? log.request_id.substring(0, 8) + '...' : '-'}
                        </td>
                        <td className="px-5 py-3 text-left">
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center gap-1"
                          >
                            <Eye size={14} />
                            Details
                          </button>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Details Modal */}
        {selectedLog && (
          <div className="fixed inset-0 bg-slate-900/50 overflow-y-auto h-full w-full z-50">
            <div className="relative min-h-full flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl border border-slate-100 max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-auto ml-64 custom-scrollbar">
                <div className="bg-slate-900 px-6 py-4 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Activity className="text-white" size={20} />
                      <div>
                        <h3 className="text-base font-bold text-white">System Log Details</h3>
                        <p className="text-slate-400 text-xs mt-0.5">Log ID: {selectedLog.id}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedLog(null)}
                      className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <X className="text-white" size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)] custom-scrollbar">
                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Clock className="text-slate-400" size={14} />
                          <span className="text-xs font-medium text-slate-500">Timestamp</span>
                        </div>
                        <p className="text-sm font-medium text-slate-900">{formatTimestamp(selectedLog.timestamp)}</p>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Activity className="text-slate-400" size={14} />
                          <span className="text-xs font-medium text-slate-500">Level</span>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full ${getLevelColor(selectedLog.level)}`}>
                          {getLevelIcon(selectedLog.level)}
                          {selectedLog.level.toUpperCase()}
                        </span>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Server className="text-slate-400" size={14} />
                          <span className="text-xs font-medium text-slate-500">Service</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {getServiceIcon(selectedLog.service)}
                          <span className="text-sm font-medium text-slate-900">{selectedLog.service}</span>
                        </div>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Database className="text-slate-400" size={14} />
                          <span className="text-xs font-medium text-slate-500">Environment</span>
                        </div>
                        <p className="text-sm font-medium text-slate-900">{selectedLog.environment}</p>
                      </div>
                    </div>

                    {/* Technical Details */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Technical Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <span className="text-xs text-slate-500">Request ID</span>
                          <p className="text-sm font-mono text-slate-900 mt-0.5">{selectedLog.request_id || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">User ID</span>
                          <p className="text-sm font-mono text-slate-900 mt-0.5">{selectedLog.user_id || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">Route</span>
                          <p className="text-sm font-mono text-slate-900 mt-0.5 break-all">{selectedLog.route || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">Method</span>
                          <p className="text-sm font-mono text-slate-900 mt-0.5">{selectedLog.method || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">Status Code</span>
                          <p className="text-sm font-mono text-slate-900 mt-0.5">{selectedLog.status_code || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">IP Address</span>
                          <p className="text-sm font-mono text-slate-900 mt-0.5">{selectedLog.ip || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">Category</span>
                          <p className="text-sm text-slate-900 mt-0.5">{selectedLog.category || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">Severity</span>
                          <p className="text-sm text-slate-900 mt-0.5">{selectedLog.severity || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Message */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-slate-900 mb-2">Message</h3>
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-sm text-slate-900">{selectedLog.message}</p>
                      </div>
                    </div>

                    {/* Stack Trace */}
                    {selectedLog.stack && (
                      <div className="bg-red-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-red-900 mb-2">Stack Trace</h3>
                        <div className="bg-white rounded-lg p-3 border border-red-200">
                          <pre className="text-xs text-red-900 whitespace-pre-wrap font-mono overflow-x-auto custom-scrollbar">
                            {selectedLog.stack}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Metadata */}
                    {selectedLog.metadata && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-slate-900 mb-2">Metadata</h3>
                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                          <pre className="text-xs text-slate-900 whitespace-pre-wrap font-mono overflow-x-auto custom-scrollbar">
                            {JSON.stringify(selectedLog.metadata, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* User Agent */}
                    {selectedLog.user_agent && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-slate-900 mb-2">User Agent</h3>
                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                          <p className="text-xs text-slate-900 font-mono break-all">{selectedLog.user_agent}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-slate-50 px-6 py-3 flex justify-end border-t border-slate-200">
                  <button
                    onClick={() => setSelectedLog(null)}
                    className="px-4 py-2.5 bg-slate-600 text-white font-medium text-sm rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white rounded-3xl border border-slate-100 px-5 py-4 mt-4">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
              <div className="text-sm text-slate-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </div>

              <div className="flex items-center gap-4">
                {/* Page size selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Show:</span>
                  <select
                    value={pagination.limit}
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value);
                      setPagination(prev => ({
                        ...prev,
                        limit: newLimit,
                        page: 1
                      }));
                    }}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                {/* Page navigation */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                    disabled={pagination.page <= 1}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page <= 1}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1 mx-1">
                    {(() => {
                      const pages = [];
                      const totalPages = pagination.pages;
                      const currentPage = pagination.page;
                      const maxVisible = 5;

                      let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                      let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                      if (endPage - startPage + 1 < maxVisible) {
                        startPage = Math.max(1, endPage - maxVisible + 1);
                      }

                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                            className="px-2.5 py-1.5 text-sm rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span key="start-ellipsis" className="px-1.5 text-slate-400 text-sm">...</span>
                          );
                        }
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setPagination(prev => ({ ...prev, page: i }))}
                            className={`px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
                              i === currentPage
                                ? 'bg-blue-600 text-white'
                                : 'hover:bg-slate-100 text-slate-600'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }

                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span key="end-ellipsis" className="px-1.5 text-slate-400 text-sm">...</span>
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => setPagination(prev => ({ ...prev, page: totalPages }))}
                            className="px-2.5 py-1.5 text-sm rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
                          >
                            {totalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= pagination.pages}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: pagination.pages }))}
                    disabled={pagination.page >= pagination.pages}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Jump to page */}
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-slate-500">Go to:</span>
                  <input
                    type="number"
                    min="1"
                    max={pagination.pages}
                    value={pagination.page}
                    onChange={(e) => {
                      const page = parseInt(e.target.value);
                      if (page >= 1 && page <= pagination.pages) {
                        setPagination(prev => ({ ...prev, page }));
                      }
                    }}
                    className="w-14 text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                    title="Enter page number"
                  />
                  <span className="text-sm text-slate-500">of {pagination.pages}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemLogs;
